<template>
  <u-modal
    v-model:visible="visible"
    :width="width"
    :top="top"
    :z-index="zIndex"
    :title="title"
    closable
    class="confirm-dialog"
    :vertical="centered"
    :mask-closable="maskClosable"
    @close="handleModalClose"
  >
    <div class="confirm-content">
      <slot>{{ content }}</slot>
    </div>
    <template #footer>
      <div
        v-if="footerVariant === 'confirm'"
        class="flex flex-1 item-center justify-center"
      >
        <u-button
          type="primary"
          class="min-w-[60px] px-4"
          :loading="loading"
          @click="handleConfirm"
          >{{ confirmText }}</u-button
        >
      </div>
      <div
        v-else
        class="flex flex-1 flex-wrap items-center justify-center gap-2"
      >
        <u-button
          class="min-w-[60px] px-4"
          @click="handleCancel"
          data-testid="components-confirmdialog-cancel-btn"
          >{{ cancelText }}</u-button
        >
        <u-button
          type="primary"
          class="min-w-[60px] px-4"
          :loading="loading"
          @click="handleSaveClick"
          >{{ saveButtonText }}</u-button
        >
      </div>
    </template>
  </u-modal>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, watch } from 'vue';

export default defineComponent({
  name: 'ConfirmDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    content: {
      type: String,
      default: ''
    },
    width: {
      type: String,
      default: '270px'
    },
    zIndex: {
      type: Number,
      default: 1000
    },
    top: {
      type: Number,
      default: undefined
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
    closeAsCancel: {
      type: Boolean,
      default: true
    },
    cancelText: {
      type: String,
      default: () => {
        const { t } = useI18n();
        return t('components.ConfirmDialog.a26368738100b51bff1d514d33927c62');
      }
    },
    confirmText: {
      type: String,
      default: () => {
        const { t } = useI18n();
        return t('components.ConfirmDialog.be15a7c160098fa4518f4f77bce3de51');
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    centered: {
      type: Boolean,
      default: false
    },
    /** confirm: single primary (legacy); saveCancel: cancel + save */
    footerVariant: {
      type: String,
      default: 'confirm',
      validator: v => ['confirm', 'saveCancel'].includes(v)
    },
    saveButtonText: {
      type: String,
      default: () => {
        const { t } = useI18n();
        return t('components.ConfirmDialog.29772b6be3724b911a02281a07c6825a');
      }
    }
  },
  emits: ['update:modelValue', 'confirm', 'cancel', 'save', 'close-icon'],
  setup(props, { emit }) {
    const visible = ref(props.modelValue);
    watch(
      () => props.modelValue,
      val => {
        visible.value = val;
      }
    );
    watch(
      () => visible.value,
      val => {
        emit('update:modelValue', val);
      }
    );

    const handleConfirm = () => {
      emit('confirm');
    };

    const handleSaveClick = () => {
      emit('save');
    };

    const handleCancel = () => {
      emit('cancel');
      visible.value = false;
    };

    const handleModalClose = () => {
      if (props.closeAsCancel) {
        handleCancel();
        return;
      }
      emit('close-icon');
      visible.value = false;
    };

    return {
      visible,
      handleConfirm,
      handleSaveClick,
      handleCancel,
      handleModalClose
    };
  }
});
</script>

<style lang="less">
.confirm-dialog {
  .uui-modal-footer {
    --modal-footer-height: 56px;
    padding-bottom: 24px;
  }
  .uui-modal-close {
    color: var(--text-neutral-color-text-tertiary);
  }
}
</style>

<style scoped lang="less">
.confirm-content {
  padding: 14px 0 10px 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: var(--text-neutral-color-text-primary);
  text-align: left;
}
</style>
